import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Receipt, Zap, Shield, TrendingUp, Check, ChevronDown, Wrench, Users, FileText, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prices, setPrices] = useState({ monthly_price: 9.99, yearly_price: 99.99, trial_days: 7 });

  useEffect(() => {
    // Fetch prices from backend
    const fetchPrices = async () => {
      try {
        const response = await api.get('/subscription/prices');
        setPrices(response.data);
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      }
    };
    fetchPrices();
  }, []);

  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-500/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Receipt className="w-12 h-12 text-indigo-400" />
              <h1 className="text-5xl md:text-7xl font-bold font-['Outfit'] tracking-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/70">
                  FinScan USA
                </span>
              </h1>
            </div>
            
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              The best receipt scanner app for taxes. Scan receipts with AI OCR, track business expenses, and organize tax deductions — perfect for freelancers, self-employed & small business.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/auth')}
                data-testid="get-started-button"
                className="bg-white text-black hover:bg-white/90 font-medium px-8 py-4 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all transform hover:scale-105 active:scale-95 text-lg"
              >
                Get Started Free
              </button>
              <button
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                data-testid="learn-more-button"
                className="bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/10 px-8 py-4 rounded-full font-medium transition-all text-lg"
              >
                Learn More
              </button>
            </div>

            {/* Industry Links */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <span className="text-sm text-muted-foreground">Perfect for:</span>
              <Link to="/electricians" className="text-sm text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                Electricians & Contractors
              </Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/1099" className="text-sm text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                1099 Workers
              </Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/freelancers" className="text-sm text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                Freelancers
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-gradient-to-b from-transparent to-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              Best Receipt Scanner for Small Business & Freelancers
            </h2>
            <p className="text-lg text-muted-foreground">
              AI-powered OCR expense tracker for tax deductions. Export to Excel, CSV, PDF.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI OCR Receipt Scanner</h3>
              <p className="text-muted-foreground">
                Snap a photo of any receipt and watch as AI extracts merchant, date, and amount in seconds. Best receipt scanner for taxes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Business Expense Tracker</h3>
              <p className="text-muted-foreground">
                Track business expenses with smart analytics. Export to Excel, CSV, PDF. Perfect for small business and self-employed.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Tax Deduction Organizer</h3>
              <p className="text-muted-foreground">
                Organize receipts for IRS tax deductions. Export reports for your CPA. Great for freelancers, contractors, and 1099 workers.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Start free. Upgrade when you're ready.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-8 bg-white/5 border border-white/10 rounded-2xl">
              <h3 className="text-2xl font-bold mb-2">Free</h3>
              <p className="text-4xl font-mono font-bold mb-6">$0<span className="text-lg text-muted-foreground">/mo</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>20 receipt scans per month</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Full analytics & reports</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Manual transactions</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="w-full bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/10 px-6 py-3 rounded-full font-medium transition-all"
              >
                Get Started
              </button>
            </div>

            <div className="p-8 bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent border border-indigo-500/30 rounded-2xl relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-indigo-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Popular
              </div>
              <h3 className="text-2xl font-bold mb-2">Pro</h3>
              <p className="text-4xl font-mono font-bold mb-6">${prices.monthly_price}<span className="text-lg text-muted-foreground">/mo</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Unlimited receipt scans</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Advanced analytics & charts</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>P&L reports & CSV export</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Priority support</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="w-full bg-white text-black hover:bg-white/90 font-medium px-6 py-3 rounded-full shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all transform hover:scale-105"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-semibold mb-4 text-white">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/auth" className="text-muted-foreground hover:text-white transition-colors">Get Started</Link></li>
                <li><a href="#features" className="text-muted-foreground hover:text-white transition-colors">Features</a></li>
                <li><Link to="/privacy-policy" className="text-muted-foreground hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">For Industries</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/electricians" className="text-muted-foreground hover:text-white transition-colors">Electricians & Contractors</Link></li>
                <li><Link to="/1099" className="text-muted-foreground hover:text-white transition-colors">1099 & Gig Workers</Link></li>
                <li><Link to="/freelancers" className="text-muted-foreground hover:text-white transition-colors">Freelancers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="text-muted-foreground hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/blog/track-expenses-1099-taxes" className="text-muted-foreground hover:text-white transition-colors">Tax Tips for 1099</Link></li>
                <li><Link to="/blog/invoice-tips-freelancers-get-paid-faster" className="text-muted-foreground hover:text-white transition-colors">Invoicing Tips</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><span className="text-muted-foreground">Built in USA</span></li>
                <li><a href="mailto:support@finscan.store" className="text-muted-foreground hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 text-center">
            <p className="text-muted-foreground text-sm">
              © 2026 FinScan USA. Built for American freelancers, contractors, and budget-conscious individuals.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
